
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Paper,
  Chip,
  Divider,
} from "@mui/material";

import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SearchIcon from "@mui/icons-material/Search";
import AssessmentIcon from "@mui/icons-material/Assessment";

import BreadcrumbsBar from "../../../component/common/BreadcrumbsBar";
import ReportCategory from "./QuartlyReportCategory";
import { reportGroups } from "./QuartlyReportsGroup";

import { procReportProcess } from "../../../services/reportService";

const quarters = [
  "First Quarter",
  "Second Quarter",
  "Third Quarter",
  "Fourth Quarter",
];

export default function QuarterlyReport() {
  const navigate = useNavigate();

  const currentDate = new Date();

  // Correctly calculate current quarter: 1 - 4
  const currentQuarter =
    Math.floor(currentDate.getMonth() / 3) + 1;

  const [selectedQuarter, setSelectedQuarter] = useState(
    () =>
      Number(sessionStorage.getItem("selectedQuarter")) ||
      currentQuarter
  );

  const [selectedQuarterYear, setSelectedQuarterYear] =
    useState(
      () =>
        Number(
          sessionStorage.getItem("selectedQuarterYear")
        ) || currentDate.getFullYear()
    );

  const [quarter, setQuarter] =
    useState(selectedQuarter);

  const [year, setYear] =
    useState(selectedQuarterYear);

  const [showReports, setShowReports] = useState(() => {
    const q = sessionStorage.getItem("selectedQuarter");
    const y = sessionStorage.getItem(
      "selectedQuarterYear"
    );

    return !!(q && y);
  });

  const [loading, setLoading] = useState(false);

  const selectedQuarterName =
    quarters[selectedQuarter - 1];

  const loadReport = async () => {
    try {
      setLoading(true);

      const res = await procReportProcess(
        selectedQuarter,
        selectedQuarterYear
      );

      alert(res.data.message);

      setQuarter(selectedQuarter);
      setYear(selectedQuarterYear);

      sessionStorage.setItem(
        "selectedQuarter",
        selectedQuarter
      );

      sessionStorage.setItem(
        "selectedQuarterYear",
        selectedQuarterYear
      );

      setShowReports(true);
    } catch (err) {
      setShowReports(false);

      alert(
        err.response?.data?.message ||
          "Failed to generate quarterly report."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleView = (report) => {
    navigate(report.route, {
      state: {
        ...report.state,
        month: quarter,
        year,
      },
    });
  };

  const totalReports = reportGroups.reduce(
    (total, group) =>
      total + group.reports.length,
    0
  );

  return (
    <Box>
      <BreadcrumbsBar page="Quarterly Reports" />

      {/* =====================================================
          PROFESSIONAL PAGE HEADER
      ===================================================== */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          mb: 3,
          p: {
            xs: 2.5,
            md: 3.5,
          },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, #f8fbff 0%, #ffffff 60%, #f4f8ff 100%)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative Circle */}
        <Box
          sx={{
            position: "absolute",
            right: -45,
            top: -55,
            width: 190,
            height: 190,
            borderRadius: "50%",
            background:
              "linear-gradient(135deg, rgba(25,118,210,0.12), rgba(25,118,210,0.02))",
          }}
        />

        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          spacing={2.5}
          alignItems={{
            xs: "flex-start",
            sm: "center",
          }}
          sx={{
            position: "relative",
            zIndex: 1,
          }}
        >
          {/* Header Icon */}
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 2.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, #1976d2, #42a5f5)",
              color: "white",
              boxShadow:
                "0 8px 20px rgba(25,118,210,0.25)",
              flexShrink: 0,
            }}
          >
            <AssessmentIcon
              sx={{
                fontSize: 34,
              }}
            />
          </Box>

          {/* Title */}
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{
                color: "text.primary",
                fontSize: {
                  xs: "1.65rem",
                  md: "2rem",
                },
              }}
            >
              Quarterly Reports Center
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{
                mt: 0.5,
                maxWidth: 680,
              }}
            >
              Generate, view and download quarterly
              reports for your selected financial period.
            </Typography>
          </Box>

          {/* Selected Period */}
          <Box
            sx={{
              minWidth: {
                xs: "100%",
                sm: 210,
              },
              textAlign: {
                xs: "left",
                sm: "right",
              },
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{
                textTransform: "uppercase",
                letterSpacing: 0.8,
              }}
            >
              Selected Period
            </Typography>

            <Typography
              variant="h6"
              fontWeight={700}
              color="primary"
              sx={{
                mt: 0.3,
              }}
            >
              {selectedQuarterName}
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              fontWeight={600}
            >
              Financial Year {selectedQuarterYear}
            </Typography>

            {showReports && (
              <Chip
                label="Reports Ready"
                size="small"
                color="success"
                variant="outlined"
                sx={{
                  mt: 0.8,
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        </Stack>
      </Paper>

      {/* =====================================================
          QUARTER SELECTOR
      ===================================================== */}
      <Paper
        elevation={0}
        className="no-print"
        sx={{
          mb: 4,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          backgroundColor: "background.paper",
        }}
      >
        {/* Selector Header */}
        <Box
          sx={{
            px: {
              xs: 2,
              md: 3,
            },
            py: 2,
            backgroundColor: "grey.50",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
          >
            <CalendarMonthIcon
              color="primary"
              sx={{
                fontSize: 27,
              }}
            />

            <Box>
              <Typography
                variant="subtitle1"
                fontWeight={700}
              >
                Report Period
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Select the quarter and financial year
                for which reports should be generated.
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* Controls */}
        <Box
          sx={{
            p: {
              xs: 2,
              md: 3,
            },
          }}
        >
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={2}
            alignItems={{
              xs: "stretch",
              sm: "center",
            }}
          >
            {/* Quarter */}
            <TextField
              select
              fullWidth
              label="Quarter"
              value={selectedQuarter}
              onChange={(e) =>
                setSelectedQuarter(
                  Number(e.target.value)
                )
              }
              sx={{
                minWidth: {
                  sm: 240,
                },
              }}
            >
              {quarters.map((quarterName, index) => (
                <MenuItem
                  key={index}
                  value={index + 1}
                >
                  {quarterName}
                </MenuItem>
              ))}
            </TextField>

            {/* Year */}
            <TextField
              label="Financial Year"
              type="number"
              value={selectedQuarterYear}
              onChange={(e) =>
                setSelectedQuarterYear(
                  Number(e.target.value)
                )
              }
              inputProps={{
                min: 2000,
                max: 2100,
              }}
              sx={{
                width: {
                  xs: "100%",
                  sm: 170,
                },
              }}
            />

            {/* Generate */}
            <Button
              variant="contained"
              size="large"
              startIcon={<SearchIcon />}
              onClick={loadReport}
              disabled={loading}
              sx={{
                minWidth: {
                  xs: "100%",
                  sm: 190,
                },
                height: 56,
                borderRadius: 2,
                fontWeight: 700,
                textTransform: "none",
                boxShadow:
                  "0 5px 14px rgba(25,118,210,0.25)",
              }}
            >
              {loading
                ? "Generating..."
                : "Generate Reports"}
            </Button>
          </Stack>
        </Box>
      </Paper>

      {/* =====================================================
          AVAILABLE REPORTS
      ===================================================== */}
      {showReports && (
        <Box>
          {/* Reports Heading */}
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
            alignItems={{
              xs: "flex-start",
              sm: "center",
            }}
            justifyContent="space-between"
            sx={{
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
              >
                Available Reports
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                Reports generated for{" "}
                <strong>
                  {selectedQuarterName}{" "}
                  {selectedQuarterYear}
                </strong>
              </Typography>
            </Box>

            <Chip
              icon={<AssessmentIcon />}
              label={`${totalReports} Reports`}
              color="primary"
              variant="outlined"
              sx={{
                fontWeight: 600,
              }}
            />
          </Stack>

          <Divider
            sx={{
              mb: 2.5,
            }}
          />

          {/* Report Categories */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(350px, 1fr))",
              gap: 2,
            }}
          >
            {reportGroups.map((group) => (
              <ReportCategory
                key={group.title}
                title={group.title}
                icon={group.icon}
                chipColor={group.chipColor}
                reports={group.reports}
                onView={handleView}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

